import { inject, injectable } from 'inversify'
import { computed, makeObservable } from 'mobx'
import { I18nRepository } from '@core/infra/i18n/i18n.repository'
import { DialogRepository, type ActiveDialog, type ConfirmOptions } from './dialog.repository'

export type { ActiveDialog, ConfirmOptions }

/**
 * Read side of the one active dialog: what DialogComponent renders, plus
 * the resolve action its buttons fire.
 *
 * Code that *asks* a question injects DialogRepository and awaits
 * `confirm()` — see the note in NotificationPresenter.
 */
@injectable()
export class DialogPresenter {
  @inject(DialogRepository)
  private dialogRepository!: DialogRepository

  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  constructor() {
    makeObservable(this, {
      dialog: computed,
      viewModel: computed,
    })
  }

  get dialog(): ActiveDialog | null {
    return this.dialogRepository.dialogPm
  }

  /**
   * Nothing null reaches the view (CI-9's out-rule, §4.3): `open` is the
   * resolved boolean the Component renders on, and every field is
   * default-filled even while closed — never `null`, which used to sit
   * directly under this comment and contradict it.
   */
  get viewModel(): { open: boolean; message: string; confirmLabel: string; cancelLabel: string } {
    const dialog = this.dialog
    if (!dialog) {
      return { open: false, message: '', confirmLabel: '', cancelLabel: '' }
    }

    return {
      open: true,
      message: dialog.message,
      confirmLabel: dialog.confirmLabel ?? this.i18nRepository.t('common.confirm'),
      cancelLabel: dialog.cancelLabel ?? this.i18nRepository.t('common.cancel'),
    }
  }

  resolve = (value: boolean) => {
    this.dialogRepository.resolve(value)
  }
}
